/**
 * Dashboard API Service
 * Handles API calls for dashboard analytics
 */

import api from './api';

export interface DashboardStats {
  date: string;
  cityId: number;
  tasks: {
    total: number;
    pickups: number;
    rtos: number;
  };
  routes: {
    total: number;
    draft: number;
    published: number;
    inProgress: number;
    completed: number;
  };
  costs: {
    totalCost: number;
    avgCostPerPickup: number;
    avgCostPerOrder: number;
    costPerParcel: number;
  };
  orders: {
    total: number;
  };
  drivers: {
    required: number;
    avgCapacityPerDriver: number;
  };
  distance: {
    totalKm: number;
  };
  duration: {
    totalMin: number;
    totalHours: number;
  };
  zones: {
    active: number;
  };
  coldChain?: {
    /** Routes carrying temperature-controlled orders. */
    routes: number;
    stops: number;
    coldOrders: number;
    /**
     * EVERY order on a cold task, not just the cold ones. A task is atomic to
     * the router, so flagging one order sends all of them to a refrigerated
     * van — this is the number a two-van fleet actually has to carry, and the
     * trigger for the wave solve (peak day above ~250).
     */
    ordersPinned: number;
    /** Refrigerated vans flagged in Schedules. Zero = the guard is unarmed. */
    capableDrivers: number;
  };
}

export interface TasksByHourData {
  date: string;
  hourlyData: Array<{
    hour: number;
    pickups: number;
    rtos: number;
    total: number;
  }>;
}

export interface TaskLocation {
  lat: number;
  lng: number;
  type: 'pickup' | 'rto' | 'cash';
  weight: number;
}

export interface TaskLocationsData {
  date: string;
  all: TaskLocation[];
  pickups: TaskLocation[];
  rtos: TaskLocation[];
  summary: {
    totalLocations: number;
    pickupLocations: number;
    rtoLocations: number;
  };
}

export interface ZoneDistributionData {
  date: string;
  cityId: number;
  zones: Array<{
    zoneId: string;
    name: string;
    color: string;
    pickups: number;
    rtos: number;
    total: number;
  }>;
  unzoned: {
    pickups: number;
    rtos: number;
    total: number;
  };
}

export interface RouteSummaryData {
  date: string;
  cityId: number;
  routes: Array<{
    routeId: string;
    driverUid: string;
    driverName: string;
    status: string;
    stopCount: number;
    pickupCount: number;
    rtoCount: number;
    distanceKm: number;
    durationMin: number;
    cost: number;
    estimatedStart: string | null;
    estimatedEnd: string | null;
  }>;
  summary: {
    totalRoutes: number;
    assignedRoutes: number;
    unassignedRoutes: number;
  };
}

/**
 * Get dashboard statistics for a specific date
 */
export async function getDashboardStats(date?: string, cityId: number = 1): Promise<DashboardStats> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  params.append('cityId', cityId.toString());
  
  const response = await api.get<{ success: boolean; data: DashboardStats }>(
    `/dashboard/stats?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get tasks by hour distribution
 */
export async function getTasksByHour(date?: string): Promise<TasksByHourData> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  
  const response = await api.get<{ success: boolean; data: TasksByHourData }>(
    `/dashboard/tasks-by-hour?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get task locations for heatmap
 */
export async function getTaskLocations(date?: string): Promise<TaskLocationsData> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  
  const response = await api.get<{ success: boolean; data: TaskLocationsData }>(
    `/dashboard/task-locations?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get zone distribution
 */
export async function getZoneDistribution(date?: string, cityId: number = 1): Promise<ZoneDistributionData> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  params.append('cityId', cityId.toString());
  
  const response = await api.get<{ success: boolean; data: ZoneDistributionData }>(
    `/dashboard/zone-distribution?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get route summary
 */
export async function getRouteSummary(date?: string, cityId: number = 1): Promise<RouteSummaryData> {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  params.append('cityId', cityId.toString());
  
  const response = await api.get<{ success: boolean; data: RouteSummaryData }>(
    `/dashboard/route-summary?${params.toString()}`
  );
  return response.data.data;
}
